import { zodResolver } from '@hookform/resolvers/zod'
import { Grid } from '@mui/joy'
import { useStore } from 'effector-react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Element } from 'react-scroll'
import { FormValues, repairOrderModel } from '~/entities/RepairOrder'
import { Car, Counterparty, RepairType, RoadAccident } from '~/shared/api'
import {
  repairOrderPaymentStatusEnumOptions,
  repairOrderStatusEnumOptions,
} from '~/shared/config/constants'
import { getObject } from '~/shared/lib/utils'
import {
  AsyncAutocompleteInput,
  AutocompleteInput,
  DatePickerInput,
  FormButtons,
  formButtonsModel,
  FormProvider,
  NumericInput,
  TextareaInput,
  TextInput,
} from '~/shared/ui/Form'
import { Container, ContainerTitle } from '~/shared/ui/Layout'
import { Files } from './Files'
import { saveFilesFx } from './model'

type FormProps = {
  isCreating?: boolean
  canEdit?: boolean
  isSubmitting?: boolean
  isLoading?: boolean
  onSuccess?: (formValues: FormValues) => void
  defaultValues?: Partial<FormValues>
  isView?: boolean
}

export function Form({
  isCreating,
  canEdit,
  isSubmitting,
  isLoading,
  defaultValues,
  onSuccess,
  isView,
}: FormProps) {
  const { t } = useTranslation()
  const isEditing = useStore(formButtonsModel.$isEditing)
  const disabled = (!isEditing && !isCreating) || isSubmitting
  const isSaveFiles = useStore(saveFilesFx.pending)

  const form = useForm<FormValues>({
    resolver: zodResolver(repairOrderModel.formSchema),
    defaultValues,
  })

  const { reset, watch, setValue } = form

  const carOption = watch('carOption')

  useEffect(() => {
    const { vin, brandModel, subdivision } = getObject(carOption) || {
      vin: '',
      brandModel: '',
      subdivision: '',
    }
    setValue('vin', vin)
    setValue('brandModel', brandModel)
    setValue('subdivision', subdivision)
    // eslint-disable-next-line
  }, [carOption])

  useEffect(() => {
    if (defaultValues) reset(defaultValues)
    // eslint-disable-next-line
  }, [defaultValues, isEditing])

  return (
    <FormProvider form={form} onSuccess={onSuccess} canEdit={canEdit}>
      <Element name='car'>
        <Container>
          <ContainerTitle>{t('form.car')}</ContainerTitle>
          <Grid container spacing={2.5}>
            <Grid xs={6}>
              <AsyncAutocompleteInput
                name='carOption'
                placeholder='form.select car'
                label='form.car'
                fetchOptions={Car.fetchWithRelationsOptions}
                skeletonShow={isLoading}
                readOnly={disabled}
              />
            </Grid>
            <Grid xs={6}>
              <TextInput
                name='vin'
                placeholder='form.vin'
                label='form.vin'
                skeletonShow={isLoading}
                readOnly
              />
            </Grid>
            <Grid xs={6}>
              <TextInput
                name='brandModel'
                placeholder='form.brand model'
                label='form.brand model'
                skeletonShow={isLoading}
                readOnly
              />
            </Grid>
            <Grid xs={6}>
              <TextInput
                name='subdivision'
                placeholder='form.subdivision'
                label='form.subdivision'
                skeletonShow={isLoading}
                readOnly
              />
            </Grid>
            <Grid xs={6}>
              <NumericInput
                name='mileage'
                placeholder='form.enter mileage'
                label='form.mileage'
                skeletonShow={isLoading}
                readOnly={disabled}
              />
            </Grid>
            {isView && (
              <Grid xs={6}>
                <TextInput
                  name='userName'
                  placeholder='form.user'
                  label='form.user'
                  skeletonShow={isLoading}
                  readOnly
                />
              </Grid>
            )}
          </Grid>
        </Container>
      </Element>

      <Element name='roadAccidents'>
        <Container>
          <ContainerTitle>{t('road accidents')}</ContainerTitle>
          <Grid container spacing={2.5}>
            <Grid xs={12}>
              <AsyncAutocompleteInput
                name='roadAccidentIds'
                placeholder='form.select road accident'
                fetchOptions={RoadAccident.fetchOptions}
                skeletonShow={isLoading}
                multiple
                readOnly={disabled}
                autocompleteXs={12}
                sx={{ '.MuiGrid-root': { padding: 0 } }}
              />
            </Grid>
          </Grid>
        </Container>
      </Element>

      <Element name='descriptionRepair'>
        <Container>
          <ContainerTitle>{t('form.description repair')}</ContainerTitle>
          <Grid container spacing={2.5}>
            <Grid container xs={6}>
              {!isView && (
                <Grid xs={12}>
                  <AutocompleteInput
                    label='form.repair order status'
                    name='status'
                    placeholder='form.select repair order status'
                    options={repairOrderStatusEnumOptions}
                    skeletonShow={isLoading}
                    readOnly={disabled}
                  />
                </Grid>
              )}
              <AsyncAutocompleteInput
                label='form.repair type'
                name='repairTypesOptions'
                placeholder='form.select repair type'
                fetchOptions={RepairType.fetchOptions}
                skeletonShow={isLoading}
                multiple
                readOnly={disabled}
                autocompleteXs={12}
              />
            </Grid>
            <Grid xs={6}>
              <TextareaInput
                name='reason'
                placeholder='form.enter reason'
                label='form.reason'
                skeletonShow={isLoading}
                readOnly={disabled}
                minRows={7}
                maxRows={7}
                sx={{
                  height: '186px',
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Element>

      <Element name='sto'>
        <Container>
          <ContainerTitle>{t('form.sto')}</ContainerTitle>
          <Grid container spacing={2.5}>
            <Grid xs={isView ? 12 : 6}>
              <AsyncAutocompleteInput
                name='counterpartyOption'
                placeholder='form.select counterparty'
                label='form.counterparty'
                fetchOptions={Counterparty.fetchOptions}
                skeletonShow={isLoading}
                readOnly={disabled}
              />
            </Grid>
            {!isView && (
              <Grid xs={6}>
                <AutocompleteInput
                  label='form.payment status'
                  name='paymentStatus'
                  placeholder='form.select payment status'
                  options={repairOrderPaymentStatusEnumOptions}
                  skeletonShow={isLoading}
                  readOnly={disabled}
                />
              </Grid>
            )}
            <Grid xs={4}>
              <DatePickerInput
                label='form.start date'
                placeholder='form.select start date'
                name='startedAt'
                skeletonShow={isLoading}
                readOnly={disabled}
              />
            </Grid>
            <Grid xs={4}>
              <DatePickerInput
                label='form.end date'
                placeholder='form.select end date'
                name='finishedAt'
                skeletonShow={isLoading}
                readOnly={disabled}
              />
            </Grid>

            <Grid xs={4}>
              <TextInput
                name='counterpartyNumber'
                placeholder='form.counterparty number'
                label='form.counterparty number'
                skeletonShow={isLoading}
                readOnly={disabled}
              />
            </Grid>
          </Grid>
        </Container>
      </Element>

      <Element name='cost'>
        <Container>
          <ContainerTitle>{t('form.cost')}</ContainerTitle>
          <Grid container spacing={2.5}>
            <Grid xs={4}>
              <NumericInput
                name='materialsCost'
                placeholder='form.enter materials cost'
                label='form.materials cost'
                skeletonShow={isLoading}
                readOnly={disabled}
              />
            </Grid>
            <Grid xs={4}>
              <NumericInput
                name='workCost'
                placeholder='form.enter work cost'
                label='form.work cost'
                skeletonShow={isLoading}
                readOnly={disabled}
              />
            </Grid>
            {isView && (
              <Grid xs={4}>
                <NumericInput
                  name='totalCost'
                  placeholder='form.total cost'
                  label='form.total cost'
                  skeletonShow={isLoading}
                  readOnly
                />
              </Grid>
            )}
          </Grid>
        </Container>
      </Element>

      <Element name='files'>
        <Container>
          <ContainerTitle>{t('files')}</ContainerTitle>
          <Files isLoading={isLoading} disabled={disabled} />
        </Container>
      </Element>

      <FormButtons
        loading={isSubmitting || isSaveFiles}
        isCreating={isCreating}
      />
    </FormProvider>
  )
}
